import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Unstable_Grid2";
import {useTheme} from '@mui/material/styles';
import { Container } from '@mui/system';
// components
import { fCurrencyEuro } from '../../utils/format-number';

import FinancialResumeTable from './financial-resume-table'
import FinancialWidgetSummary from "./financial-widget-summary";
import { getFinancialData } from '../../components/upload';
import { useSettingsContext } from '../../components/settings';
import AnalyticsWidgetSummary from './analytics-widget-summary';
import Iconify from '../../components/iconify';

export default function FinancialSaldo() {
    const navigate = useNavigate();
    const settings = useSettingsContext();
    const [resumen, setResumen] = useState(() => settings.saldoCache?.resumen || null);
    const [saldoTableData, setSaldoTableData] = useState(() => settings.saldoCache?.tableData || null);
    
    useEffect(() => {
        if (resumen){
            return;
        }

        try {
            const data = getFinancialData();
            if (!data) throw new Error('No data');
            setResumen(data);
            settings.onFiles(); // Solo si tienes datos
            settings.onValid();

                
            let firstSaldoIndex = 0;
            for (let i = 0; i < data.fecha.length; i += 1) {
                if (data.saldo.saldo_inicial[i] !== 0 && data.saldo.saldo_final[i] !== 0) {
                    firstSaldoIndex = i;
                    break;
                }
            }

            for (let i = firstSaldoIndex + 1; i < data.fecha.length; i += 1) {
                if (data.saldo.saldo_inicial[i] === 0 && data.saldo.saldo_final[i] === 0) {
                    data.saldo.saldo_inicial[i] = data.saldo.saldo_final[i - 1]
                    data.saldo.saldo_final[i] = data.saldo.saldo_final[i - 1];
                    break;
                }
            }

            const saldoTable = data.fecha.map((_, i) => ({
                date: data.fecha[i],
                startBalance: data.saldo.saldo_inicial[i],
                abono: data.abono.historico[i].abono,
                gasto: data.gasto.historico[i].gasto,
                ahorro: (data.abono.historico[i].abono + data.gasto.historico[i].gasto) === 0 ? '0' : data.saldo.saldo_final[i] - data.saldo.saldo_inicial[i],
                endBalance: data.saldo.saldo_final[i],
            })).filter((_, i) => i >= firstSaldoIndex).reverse();

            setSaldoTableData(saldoTable)

            const nuevoCache = {
                resumen: data,
                tableData: saldoTable
            }

            settings.setSaldoCache(nuevoCache)
        } catch (error) {
            console.warn('No se encontraron datos. Redirigiendo al inicio...');
            navigate('/financial');
        }
    }, [navigate, settings, resumen]);
    
    if (!resumen) return null;

    const fechas = Object.keys(resumen.saldo.saldo_diario); 
    const valores = Object.values(resumen.saldo.saldo_diario);

    return (
        <Container maxWidth='xl'>
            <Stack spacing={3}>
                <Grid container spacing={3}>
                    <Grid item xs={6} md={3}>
                        <AnalyticsWidgetSummary
                        title="Último saldo disponible"
                        total={Math.floor(resumen.saldo.saldo_total_actual) || '0'}
                        funct={fCurrencyEuro}
                        icon={<Iconify icon="mdi:cash" width={64} />}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <AnalyticsWidgetSummary
                        title="Saldo promedio mínimo diario"
                        tooltipText='Últimos 6 meses'
                        total={Math.floor(resumen.saldo.saldo_promedio) || '0'}
                        funct={fCurrencyEuro}
                        icon={<Iconify icon="mdi:chart-line" width={64} />}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <AnalyticsWidgetSummary
                        title="Saldo máximo"
                        tooltipText='Últimos 6 meses'
                        total={Math.floor(resumen.saldo.saldo_maximo_reconstruido) || '0'}
                        funct={fCurrencyEuro}
                        icon={<Iconify icon="mdi:arrow-up-bold" width={64} />}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <AnalyticsWidgetSummary
                        title="Saldo mínimo"
                        tooltipText='Últimos 6 meses'
                        total={Math.floor(resumen.saldo.saldo_minimo_reconstruido) || '0'}
                        funct={fCurrencyEuro}
                        icon={<Iconify icon="mdi:arrow-down-bold" width={64} />}
                        />
                    </Grid>
                </Grid>
                <Stack spacing={3}>
                    <Card>
                    <FinancialWidgetSummary
                        title="Evolución del saldo mínimo diario"
                        bigtitle
                        chart={{
                            series: fechas.map((fecha, index) => ({
                                x: fecha,
                                y: valores[index]
                            })),
                        }}
                    />
                    </Card>

                    <Grid xs={12} md={8}>
                        <FinancialResumeTable
                            title="Resumen mensual"
                            tableData={saldoTableData}
                            tableLabels={[
                                { id: 'date', label: 'Fecha' },
                                { id: 'startBalance', label: 'Saldo inicial' },
                                { id: 'abono', label: 'Ingresos' },
                                { id: 'gasto', label: 'Gastos' },
                                { id: 'ahorro', label: 'Ahorro' },
                                { id: 'endBalance', label: 'Saldo final' },
                            ]}
                        />
                    </Grid>
                </Stack>
            </Stack>
        </Container>
    );
}